<template>
  <div>
    <button
      v-if="isMobile && !isMonogram"
      :class="[$blockClass(blockClass, 'quick-add'), rootClass]"
      class="quick-add"
      @mousedown="loadComponent('quick-add-mobile')"
      @touchstart="loadComponent('quick-add-mobile')"
      @click.prevent="handleOpenMobileQuickAdd"
    >
      <slot name="quickaddContent">
        <span v-html="$icon(`misc.cart`)" />
      </slot>
    </button>

    <div
      v-else-if="!isMonogram"
      :class="[
        $blockClass(blockClass, 'quick-add'),
        rootClass
      ]"
      class="quick-add"
      @mouseleave="state = defaultState"
    >
      <!-- Default state -->
      <button
        v-if="product.available && !productCardColor"
        v-show="state === 'default'"
        class="quick-add__button"
        @mouseenter="state = nonColors?.values.length ? 'nonColor' : 'color'"
      >
        {{ $string('product.quick_add.quick_add') }}
        <span
          :aria-label="$string('product.quick_add.quick_add')"
          v-html="$icon('misc.cart')"
        />
      </button>

      <!-- If product as whole is available AND specific color IS available -->
      <button
        v-else-if="product.available && productCardColor && productHelpers.isVariantOptionAvailable(product, [selectedColor, option])"
        v-show="state === 'default'"
        class="quick-add__button"
        @mouseenter="state = nonColors?.values.length ? 'nonColor' : 'color'"
      >
        {{ $string('product.quick_add.quick_add') }}
        <span
          :aria-label="$string('product.quick_add.quick_add')"
          v-html="$icon('misc.cart')"
        />
      </button>

      <!-- If product as whole is available but specific color is NOT available -->
      <button
        v-else-if="product.available && productCardColor && !productHelpers.isVariantOptionAvailable(product, [selectedColor, option])"
        v-show="state === 'default'"
        class="quick-add__button"
        :disabled="true"
        @click.prevent="handleAddToCart"
      >
        {{ $string('product.cta.sold_out') }}
      </button>

      <!-- Default state when product is NOT available-->
      <button
        v-else-if="!product.available"
        v-show="state === 'default'"
        class="quick-add__button"
        :disabled="true"
        @click.prevent="handleAddToCart"
      >
        {{ $string('product.cta.sold_out') }}
      </button>

      <!-- Non color state (e.g. sizes) -->
      <div
        v-if="nonColors"
        v-show="state === 'nonColor'"
        class="quick-add__button quick-add__button--non-color"
      >
        <div
          v-if="maxPages > 1"
          v-show="pagination.direction === 'prev'"
        >
          <button
            class="quick-add__change-slide quick-add__change-slide--prev"
            @click.prevent="changeSlide('prev')"
            v-html="$icon('direction.arrow.left')"
          />
        </div>

        <div class="quick-add__swiper">
          <div
            ref="optionSlides"
            class="quick-add__swiper-wrapper"
          >
            <div
              v-for="(option, index) in nonColors.values"
              :key="`non-color-slide-${namespace}-${index}`"
              class="quick-add__swiper-slide"
            >
              <button
                class="quick-add__size-button"
                :disabled="!productHelpers.isVariantOptionAvailable(product, [selectedColor, option])"
                @click.prevent="
                  updateSelectedOptions(nonColors.index, option);
                  handleAddToCart($event, option)
                "
              >
                {{ option }}
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="maxPages > 1"
          v-show="pagination.direction === 'next'"
        >
          <button
            class="quick-add__change-slide quick-add__change-slide--next"
            @click.prevent="changeSlide('next')"
            v-html="$icon('direction.arrow.right')"
          />
        </div>
      </div>

      <!-- Color or default variant only state -->
      <button
        v-if="productCardColor || product.hasOnlyDefaultVariant"
        v-show="state === 'color'"
        class="quick-add__button quick-add__add-cart"
        :disabled="!selectedVariant?.available"
        @click.prevent="handleAddToCart"
        v-html="ctaLabel"
      />
    </div>

    <!-- Mobile modal -->
    <overlay
      v-if="isMobile && !isMonogram"
      :namespace="namespace"
      :template="false"
      block-class="quick-add-overlay"
      direction="up"
      type="drawer"
      :teleport-content="true"
    >
      <template #body="{ isActive }">
        <quick-add-mobile
          v-cloak
          v-if="getComponentVisible('quick-add-mobile')"
          :is-active="isActive"
          :namespace="namespace"
          :preselected-options="preselectedOptions"
          :product="product"
          :product-card-color="productCardColor"
          :wishlist="wishlist"
          @update-selected-variant="
            variant => $emit('update-selected-variant', variant)
          "
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Quick add
 * -----------------------------------------------------------------------------
 * Quick add component on product cards.
 *
 * @param {String} blockClass - A block class to append to component elements.
 * @param {Object} preselectedOptions - Preselected options to pass to the
 * mobile overlay.
 * @param {Object} product - The product to display quick add options for.
 * @param {String} productCardColor - The selected color on the product card.
 * @param {String} namespace - A unique namespace for the quick add overlay.
 * @param {Boolean} wishlist - Enable wishlist button.
 */

import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import { getTranslateString, getTranslateX } from '~/helpers/general'
import * as productHelpers from '~/helpers/product'

export default {
  name: 'QuickAdd',

  components: {
    QuickAddMobile: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/quick-add-mobile/quick-add-mobile'
        ),
    }),
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    preselectedOptions: {
      type: Array,
      default: () => ([]),
    },
    product: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    productCardColor: {
      type: String,
      default: '',
    },
    namespace: {
      type: String,
      default: '',
    },
    rootClass: {
      type: String,
      default: '',
    },
    wishlist: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update-selected-variant'],

  data() {
    return {
      type: 'filter',
      colorIndex: -1,
      global: true,
      isAddingToCart: false,
      selectedOptions: [],
      pagination: {
        current: 1,
        direction: 'next',
      },
      productHelpers,
      state: 'default',
      nonColors: productHelpers.getNonColors(this.product),
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute the add to cart button label.
     * @returns {String}
     */
    ctaLabel() {
      if (this.isAddingToCart) {
        return this.$icon('misc.loading')
      }
      else if (this.selectedVariant?.available) {
        if (productHelpers.isPreorder(this.product, this.selectedVariant)) {
          return this.$string('product.cta.pre_order')
        } else {
          return this.$string('product.cta.add_to_cart')
        }
      }
      else {
        return this.$string('product.cta.sold_out')
      }
    },

    defaultState() {
      if (!this.selectedVariant?.available && this.colorIndex > -1 && !this.nonColors) {
        return 'color'
      }
      else {
        return 'default'
      }
    },

    isMonogram() {
      return this.product.tags.filter(tag => tag.match(/monogram/gi)).length
    },

    /**
     * Compute the max number of pages for non-color pills (3 per page)
     * @returns {number}
     */
    maxPages() {
      if (!this.nonColors) {
        return 0
      }

      return Math.ceil(this.nonColors.values.length / 3)
    },

    selectedColor() {
      return this.selectedOptions[this.colorIndex]?.value
    },

    /**
     * Compute the selected variant based on the selected options.
     * @returns {Object}
     */
    selectedVariant() {
      return productHelpers.getVariantByTitle(
        this.product, this.selectedOptions,
      )
    },
  },

  watch: {

    /**
     * Watch the current page in the pill slider.
     */
    'pagination.current': function() {
      const { direction, current } = this.pagination

      if (direction === 'next' && current === this.maxPages) {
        this.pagination.direction = 'prev'
      }
      else if (direction === 'prev' && current === 1) {
        this.pagination.direction = 'next'
      }
    },

    /**
     * Watch the product card selected color to update quick add.
     */
    productCardColor() {
      this.updateColor(this.productCardColor)
    },

    selectedVariant() {
      this.state = this.defaultState
    },
  },

  mounted() {

    /**
     * Set the initial options
     */
    this.selectedOptions = this.product.options.map(
      option => {
        if (option.name.match(/color/gi)) {
          this.colorIndex = option.index
        }
        return { name: option.name, value: '' }
      },
    )

    if (this.productCardColor) {
      this.updateColor(this.productCardColor)
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      loadComponent: 'components/load',
      openOverlay: 'overlays/open',
    }),

    /**
     * Add to cart and open the cart drawer.
     */
    async handleAddToCart(event, option) {
      if (this.isAddingToCart) {
        return
      }
      try {
        const properties = productHelpers.getLineItemProperties(this.product, this.selectedVariant)

        if (event && option) {
          this.updateSizeButtonLabel(event.target, option, true)
        }

        this.isAddingToCart = true

        await this.addItem([
          {
            id: this.selectedVariant?.id,
            quantity: 1,
            properties,
            variant: this.selectedVariant,
            product: this.product,
          },
        ])

        let type = 'filter'

        if (window.cnvs.page.type === 'search') {
          type = 'search'
        }

        this.type = type

        if (event && option) {
          this.updateSizeButtonLabel(event.target, option)
        }

        this.isAddingToCart = false

        this.openOverlay({
          namespace: 'cartDrawer',
          ignoreDismissed: true,
          component: 'cart-drawer',
        })

        this.$gtmTrack('AddtoCart', this.$variable('analytics.quickAdd'))
      }
      catch (error) {
        this.isAddingToCart = false
        if (event && option) {
          this.updateSizeButtonLabel(event.target, option)
        }
        console.error('[QuickAddDesktop]', error)
      }
    },

    /**
     * Open mobile quick add and track event
     */
    handleOpenMobileQuickAdd() {
      this.openOverlay({
        component: 'quick-add-mobile',
        ignoreDismissed: true,
        namespace: this.namespace,
      })

      this.$gtmTrack('open_quick_view', 'product_tile_quick_view', {
        product: {
          title: this.product.title,
          handle: this.product.handle,
        },
      })
    },

    /**
     * Move slides left or right and update the current page.
     * @param {String} direction - 'prev' or 'next'
     */
    changeSlide(direction) {
      const element = this.$refs.optionSlides
      let translateX = getTranslateX(element)

      if (direction === 'next') {
        translateX -= 100
        this.pagination.current++
      }
      else if (direction === 'prev') {
        translateX += 100
        this.pagination.current--
      }

      element.style.transform = getTranslateString(translateX)
    },

    /**
     * Update the current color if it exists.
     * @param {String} value - The new color value.
     */
    updateColor(value) {
      if (this.colorIndex < 0) {
        return
      }
      this.selectedOptions[this.colorIndex].value = value || ''
    },

    /**
     * Update the selected options.
     * @param {Number} index - The option index from product.options
     * @param {String} value - The new option value.
     */
    updateSelectedOptions(index, value) {
      this.selectedOptions[index].value = value
    },

    /**
     * Update the size label to show loading state when clicked.
     * @param {HTMLElement} element - The clicked button.
     * @param {String} option - The original option text.
     * @param {Boolean} loading - Show loading?
     */
    updateSizeButtonLabel(element, option, loading = false) {
      element.innerHTML = loading ? this.$icon('misc.loading') : option
    },
  },
}
</script>
